// CallDetailModal.jsx - Enhanced with timestamps, interruptions, and cut-off highlighting
// Version: 3.97-pre - Modernized UI with Redstone theme colors, audio player, keyword badges

import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Phone, User, Bot, MessageSquare, Tag, Volume2, Zap, UserX, Play, Pause, Download } from 'lucide-react'
import { monitoringApi, API_BASE_URL } from '../services/api'

// Redstone Theme Colors - matching CallLogsTab
const THEME_COLORS = {
    transfer: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.3)' },
    voicemail: { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.3)' },
    notInterested: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)' },
    dnc: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)' },
    noAnswer: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', border: 'rgba(107, 114, 128, 0.3)' },
    callback: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.3)' },
    noise: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.15)', border: 'rgba(100, 116, 139, 0.3)' },
    howAreYou: { color: '#06b6d4', bg: 'rgba(6, 182, 212, 0.15)', border: 'rgba(6, 182, 212, 0.3)' },
    default: { color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.15)', border: 'rgba(148, 163, 184, 0.3)' }
}

// Map keyword categories to theme colors
const getKeywordCategoryColor = (category) => {
    const cat = (category || '').toLowerCase().replace(/[_\s]/g, '-')

    // Yes/Positive/Transfer keywords
    if (cat.includes('yes') || cat.includes('positive') || cat.includes('transfer') || cat.includes('ok-continue')) {
        return THEME_COLORS.transfer
    }
    // Answering Machine / Voicemail
    if (cat.includes('am') || cat.includes('answering') || cat.includes('voicemail') || cat.includes('machine')) {
        return THEME_COLORS.voicemail
    }
    // Not Interested
    if (cat.includes('not-interested') || cat.includes('no-interest')) {
        return THEME_COLORS.notInterested
    }
    // DNC
    if (cat.includes('dnc') || cat.includes('do-not-call')) {
        return THEME_COLORS.dnc
    }
    // No Answer / DNQ
    if (cat.includes('no-answer') || cat.includes('dnq') || cat.includes('qualify')) {
        return THEME_COLORS.noAnswer
    }
    // Callback
    if (cat.includes('callback') || cat.includes('call-back')) {
        return THEME_COLORS.callback
    }
    // How Are You
    if (cat.includes('how-are-you') || cat.includes('greeting')) {
        return THEME_COLORS.howAreYou
    }
    // Noise / Filler words
    if (cat.includes('noise') || cat.includes('filler') || cat.includes('already')) {
        return THEME_COLORS.noise
    }

    return THEME_COLORS.default
}

// Map disposition to theme color
const getDispositionColor = (dispo) => {
    const d = (dispo || '').toLowerCase().replace(/[_\s]/g, '-')

    if (d.includes('transfer') || d.includes('success')) return THEME_COLORS.transfer
    if (d.includes('voicemail') || d.includes('answering') || d.includes('machine')) return THEME_COLORS.voicemail
    if (d.includes('not-interested')) return THEME_COLORS.notInterested
    if (d.includes('dnc') || d.includes('do-not-call')) return THEME_COLORS.dnc
    if (d.includes('no-answer') || d.includes('dnq') || d.includes('busy')) return THEME_COLORS.noAnswer
    if (d.includes('callback') || d.includes('call-back')) return THEME_COLORS.callback

    return THEME_COLORS.default
}

// Human-readable labels
const DISPOSITION_LABELS = {
    'not-interested': 'Not Interested',
    'answering-machine': 'Answering Machine',
    'do-not-call': 'Do Not Call',
    'no-answer': 'No Answer',
    'call-back': 'Call Back',
    'customer-busy': 'Customer Busy',
    'age-do-not-qualify': 'Age - Do Not Qualify',
    'transfer-call': 'Transfer Call',
    'call-transfered': 'Call Transferred',
    'busy': 'Busy',
    'dnq': 'Do Not Qualify'
}

const formatLabel = (str) => {
    if (!str) return 'Unknown'
    if (DISPOSITION_LABELS[str]) return DISPOSITION_LABELS[str]
    if (DISPOSITION_LABELS[str.toLowerCase()]) return DISPOSITION_LABELS[str.toLowerCase()]
    return str.replace(/[-_]/g, ' ').split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
}

export default function CallDetailModal({ callId, disposition, onClose }) {
    const [events, setEvents] = useState([])
    const [keywords, setKeywords] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [voiceSetMetadata, setVoiceSetMetadata] = useState({})

    // Audio player state
    const audioRef = useRef(null)
    const [isPlaying, setIsPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [playbackSpeed, setPlaybackSpeed] = useState(1)

    useEffect(() => {
        const fetchCallDetails = async () => {
            try {
                setLoading(true)
                setError(null)

                // Use the enhanced call-events endpoint which includes FG/BG context
                const transcriptRes = await monitoringApi.getCallEvents(callId)
                const eventData = transcriptRes.data?.data?.timeline || transcriptRes.data?.timeline || transcriptRes.data?.events || transcriptRes.data?.data || transcriptRes.data || []

                // Sort events: when timestamps are equal, caller transcription should appear before bot audio
                // (because caller speech triggers the bot response)
                const sortedEvents = (Array.isArray(eventData) ? eventData : []).sort((a, b) => {
                    const timeA = a.time ?? a.call_duration_at_event ?? 0
                    const timeB = b.time ?? b.call_duration_at_event ?? 0

                    if (timeA !== timeB) return timeA - timeB

                    // Same timestamp: prioritize transcription before bot_audio
                    const eventType = (e) => e.type || e.event_type
                    const priority = { 'transcription': 1, 'bot_audio': 2, 'interruption': 3 }
                    const prioA = priority[eventType(a)] || 99
                    const prioB = priority[eventType(b)] || 99
                    return prioA - prioB
                })

                // Fix caller steps by tracking the current step as we iterate
                // Bot events have correct steps, caller transcriptions often have step=1
                let currentStep = 1
                const eventsWithFixedSteps = sortedEvents.map(event => {
                    const eventType = event.type || event.event_type
                    const eventStep = event.step ?? event.script_step

                    // Bot audio always has correct step - use it to track current step
                    if (eventType === 'bot_audio' && eventStep) {
                        currentStep = eventStep
                    }

                    // For transcriptions with step=1, use the current tracked step instead
                    if (eventType === 'transcription') {
                        return {
                            ...event,
                            script_step: eventStep === 1 ? currentStep : eventStep,
                            // Normalize the event structure
                            event_type: eventType,
                            call_duration_at_event: event.time ?? event.call_duration_at_event,
                            context: event.context || (event.isBackground ? 'background' : 'final')
                        }
                    }

                    // Normalize other events
                    return {
                        ...event,
                        event_type: eventType,
                        call_duration_at_event: event.time ?? event.call_duration_at_event,
                        script_step: eventStep
                    }
                })

                setEvents(eventsWithFixedSteps)

                // Fetch voice set metadata
                let metadata = {}
                if (disposition?.voice_set_id) {
                    try {
                        const metaRes = await fetch(`${API_BASE_URL}/voice-sets/${disposition.voice_set_id}/files-with-metadata`)
                        const metaData = await metaRes.json()
                        if (metaData.files) {
                            metaData.files.forEach(file => {
                                if (file.category_name && file.transcription && !metadata[file.category_name]) {
                                    metadata[file.category_name] = file.transcription
                                }
                                if (file.display_name && file.transcription) {
                                    metadata[file.display_name] = file.transcription
                                }
                            })
                        }
                    } catch (e) { console.log('Could not load voice set metadata') }
                }
                setVoiceSetMetadata(metadata)

                try {
                    const keywordsRes = await monitoringApi.getCallKeywords(callId)
                    const keywordData = keywordsRes.data?.data || keywordsRes.data || []
                    setKeywords(Array.isArray(keywordData) ? keywordData : [])
                } catch (kwErr) { setKeywords([]) }

            } catch (err) {
                console.error('Failed to load call details:', err)
                setError(err.message)
            } finally {
                setLoading(false)
            }
        }
        if (callId) fetchCallDetails()
    }, [callId, disposition?.voice_set_id])

    // Audio player event handlers
    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const handleTimeUpdate = () => {
            setCurrentTime(audio.currentTime)
            // Also try to get duration from playing audio if not yet set
            if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                setDuration(prev => prev > 0 ? prev : audio.duration)
            }
        }
        const handleLoadedMetadata = () => {
            if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration)
            }
        }
        const handleDurationChange = () => {
            if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
                setDuration(audio.duration)
            }
        }
        const handleEnded = () => setIsPlaying(false)

        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('loadedmetadata', handleLoadedMetadata)
        audio.addEventListener('durationchange', handleDurationChange)
        audio.addEventListener('ended', handleEnded)

        // Use call_duration as fallback if available
        if (disposition?.call_duration && disposition.call_duration > 0) {
            setDuration(prev => prev > 0 ? prev : disposition.call_duration)
        }

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate)
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
            audio.removeEventListener('durationchange', handleDurationChange)
            audio.removeEventListener('ended', handleEnded)
        }
    }, [disposition?.recording_url, disposition?.call_duration])

    const togglePlayPause = () => {
        const audio = audioRef.current
        if (!audio) return

        if (isPlaying) {
            audio.pause()
        } else {
            audio.play()
        }
        setIsPlaying(!isPlaying)
    }

    const handleSeek = (e) => {
        const audio = audioRef.current
        if (!audio) return

        // Use actual duration or fallback to call_duration
        const seekDuration = (audio.duration && isFinite(audio.duration) && audio.duration > 0)
            ? audio.duration
            : (duration || disposition?.call_duration || 0)

        if (!seekDuration) return

        const rect = e.currentTarget.getBoundingClientRect()
        const clickX = e.clientX - rect.left
        const newTime = Math.max(0, Math.min((clickX / rect.width) * seekDuration, seekDuration))

        try {
            audio.currentTime = newTime
            setCurrentTime(newTime)
        } catch (err) {
            console.log('Seek failed:', err)
        }
    }

    const handleSpeedChange = () => {
        const speeds = [0.5, 1, 1.5, 2]
        const currentIndex = speeds.indexOf(playbackSpeed)
        const nextIndex = (currentIndex + 1) % speeds.length
        const newSpeed = speeds[nextIndex]
        setPlaybackSpeed(newSpeed)
        if (audioRef.current) {
            audioRef.current.playbackRate = newSpeed
        }
    }

    const formatAudioTime = (time) => {
        if (!time || isNaN(time) || !isFinite(time)) return '0:00'
        const mins = Math.floor(time / 60)
        const secs = Math.floor(time % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    // Format seconds as "0:05" or "1:30"
    const formatTimestamp = (seconds) => {
        if (seconds === null || seconds === undefined) return ''
        const secs = parseInt(seconds)
        if (isNaN(secs)) return ''
        const mins = Math.floor(secs / 60)
        const remainingSecs = secs % 60
        return `${mins}:${remainingSecs.toString().padStart(2, '0')}`
    }

    const formatDuration = (seconds) => {
        if (!seconds) return '0s'
        const secs = parseInt(seconds)
        const mins = Math.floor(secs / 60)
        const remainingSecs = secs % 60
        return mins > 0 ? `${mins}m ${remainingSecs}s` : `${secs}s`
    }

    const getBotTranscription = (audioName) => {
        if (!audioName) return null
        if (voiceSetMetadata[audioName]) return voiceSetMetadata[audioName]
        const nameNoExt = audioName.replace(/\.wav$/i, '')
        if (voiceSetMetadata[nameNoExt]) return voiceSetMetadata[nameNoExt]
        return null
    }

    // Parse interruption metadata
    const parseInterruptionMetadata = (event) => {
        if (event.metadata) {
            try { return JSON.parse(event.metadata) } catch (e) { return null }
        }
        const match = event.content?.match(/Cut off: "([^"]+)"/)
        if (match) return { cut_off_text: match[1] }
        return null
    }

    const formatContent = (event) => {
        if (event.event_type === 'call_start') return 'Call started'
        if (event.event_type === 'call_end') {
            const match = event.content?.match(/Disposition:\s*(.+)/)
            if (match) return `Call ended: ${formatLabel(match[1].trim())}`
            return event.content || 'Call ended'
        }
        if (event.event_type === 'bot_audio') {
            const transcription = getBotTranscription(event.content)
            if (transcription) return transcription
            return event.content
        }
        return event.content
    }

    // Check if next event is interruption
    const getInterruptionForBotAudio = (index) => {
        const nextEvent = events[index + 1]
        if (nextEvent && nextEvent.event_type === 'interruption') {
            return parseInterruptionMetadata(nextEvent)
        }
        return null
    }

    // Render bot audio with interruption highlighting
    const renderBotAudio = (event, index) => {
        const transcription = getBotTranscription(event.content)
        const interruptMeta = getInterruptionForBotAudio(index)

        if (transcription) {
            if (interruptMeta && interruptMeta.cut_off_text) {
                const cutText = interruptMeta.cut_off_text
                const cutIndex = transcription.indexOf(cutText)

                if (cutIndex !== -1) {
                    const beforeCut = transcription.substring(0, cutIndex)
                    const cutPart = transcription.substring(cutIndex)

                    return (
                        <span>
                            <span style={{ color: 'var(--text-primary, #fff)' }}>"{beforeCut}</span>
                            <span style={styles.cutOffText}>{cutPart}</span>
                            <span style={{ color: 'var(--text-primary, #fff)' }}>"</span>
                            <span style={styles.interruptedBadge}>⚡ CUT</span>
                        </span>
                    )
                }
            }
            return <span style={{ color: 'var(--text-secondary, #a0a0a0)', fontStyle: 'italic' }}>"{transcription}"</span>
        }

        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)' }}>
                <Volume2 size={14} />
                {event.content}
            </span>
        )
    }

    // Aggregate keywords
    const aggregatedKeywords = keywords.reduce((acc, kw) => {
        const category = kw.keyword_category
        if (!acc[category]) {
            acc[category] = { category, count: 0, triggeredStepChange: false, keywords: [] }
        }
        acc[category].count++
        if (kw.triggered_step_change) acc[category].triggeredStepChange = true
        if (kw.keyword_text && !acc[category].keywords.includes(kw.keyword_text)) {
            acc[category].keywords.push(kw.keyword_text)
        }
        return acc
    }, {})

    const keywordList = Object.values(aggregatedKeywords)

    // Inline styles
    const styles = {
        timestamp: {
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            minWidth: '45px',
            textAlign: 'right',
            marginRight: '12px',
            fontWeight: 500
        },
        messageRow: {
            display: 'flex',
            alignItems: 'flex-start',
            padding: '10px 0',
            borderBottom: '1px solid rgba(255,255,255,0.05)'
        },
        cutOffText: {
            color: '#ff6b6b',
            backgroundColor: 'rgba(255, 107, 107, 0.2)',
            padding: '0 3px',
            borderRadius: '2px',
            textDecoration: 'line-through',
            textDecorationColor: '#ff6b6b',
            fontStyle: 'italic'
        },
        interruptedBadge: {
            display: 'inline-block',
            backgroundColor: '#ff6b6b',
            color: 'white',
            fontSize: '0.6rem',
            padding: '2px 5px',
            borderRadius: '3px',
            marginLeft: '8px',
            fontWeight: 'bold',
            verticalAlign: 'middle'
        },
        interruptionRow: {
            backgroundColor: 'rgba(255, 193, 7, 0.15)',
            borderLeft: '3px solid #ffc107',
            padding: '8px 12px',
            margin: '4px 0',
            borderRadius: '0 6px 6px 0',
            fontSize: '0.85rem',
            color: '#ffc107',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        senderBot: {
            color: 'var(--primary, #6366f1)',
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
        },
        senderCaller: {
            color: 'var(--success, #22c55e)',
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
        },
        senderCallerBG: {
            color: '#f59e0b',  // Amber for background speech
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
        },
        senderSystem: {
            color: 'var(--text-muted)',
            fontWeight: 600,
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
        },
        stepBadge: {
            fontSize: '0.65rem',
            color: 'var(--text-muted)',
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '1px 5px',
            borderRadius: '3px',
            marginLeft: '6px'
        }
    }

    const getSenderStyle = (event) => {
        if (event.event_type === 'bot_audio' || event.speaker === 'bot') return styles.senderBot
        if (event.event_type === 'transcription' || event.speaker === 'caller') {
            // Check if this is background speech
            return event.context === 'background' ? styles.senderCallerBG : styles.senderCaller
        }
        return styles.senderSystem
    }

    const getSenderLabel = (event) => {
        if (event.event_type === 'bot_audio' || event.speaker === 'bot') return 'Bot'
        if (event.event_type === 'transcription' || event.speaker === 'caller') {
            // Show context indicator for background speech
            return event.context === 'background' ? 'Caller (BG)' : 'Caller'
        }
        return 'System'
    }

    const getIcon = (event) => {
        if (event.event_type === 'bot_audio' || event.speaker === 'bot') return <Bot size={14} />
        if (event.event_type === 'transcription' || event.speaker === 'caller') return <User size={14} />
        return <MessageSquare size={14} />
    }

    return (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="call-detail-modal"
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    onClick={e => e.stopPropagation()}
                    style={{ maxWidth: '750px', width: '95%' }}
                >
                    {/* Header */}
                    <div className="modal-header">
                        <div className="modal-title">
                            <Phone size={20} />
                            <span>Call Transcript</span>
                        </div>
                        <button className="close-btn" onClick={onClose}><X size={20} /></button>
                    </div>

                    {/* Call Info Bar */}
                    {disposition && (
                        <div className="call-info" style={{
                            display: 'flex',
                            gap: '24px',
                            padding: '16px 20px',
                            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.04) 100%)',
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            flexWrap: 'wrap'
                        }}>
                            <div><span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</span><br /><span style={{ fontFamily: 'monospace', fontWeight: 500 }}>{disposition.phone_number || disposition.phone_masked}</span></div>
                            <div><span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Duration</span><br /><span style={{ fontWeight: 500 }}>{formatDuration(disposition.call_duration)}</span></div>
                            <div><span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Steps</span><br /><span style={{ fontWeight: 500 }}>{disposition.script_step_reached}</span></div>
                            <div><span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hung Up</span><br />
                                <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    fontWeight: 500,
                                    color: disposition.hung_up_by === 'caller' ? '#f87171' : disposition.hung_up_by === 'agent' ? '#60a5fa' : 'var(--text-muted)'
                                }}>
                                    {disposition.hung_up_by === 'caller' ? <><UserX size={14} /> Caller</> :
                                     disposition.hung_up_by === 'agent' ? <><Bot size={14} /> Agent</> : '—'}
                                </span>
                            </div>
                            <div><span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Result</span><br />
                                {(() => {
                                    const dispoColor = getDispositionColor(disposition.disposition)
                                    return (
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            background: dispoColor.bg,
                                            color: dispoColor.color,
                                            border: `1px solid ${dispoColor.border}`
                                        }}>
                                            {formatLabel(disposition.disposition)}
                                        </span>
                                    )
                                })()}
                            </div>
                        </div>
                    )}

                    {/* Audio Player */}
                    {disposition?.recording_url && (
                        <div style={{
                            padding: '14px 20px',
                            background: '#1e1e1e',
                            borderBottom: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <audio ref={audioRef} src={disposition.recording_url} preload="metadata" />
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '14px'
                            }}>
                                {/* Play/Pause Button */}
                                <button
                                    onClick={togglePlayPause}
                                    style={{
                                        width: '38px',
                                        height: '38px',
                                        borderRadius: '50%',
                                        background: 'var(--primary, #dc2626)',
                                        border: 'none',
                                        color: 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        transition: 'transform 0.15s, background 0.15s'
                                    }}
                                    onMouseOver={e => e.currentTarget.style.transform = 'scale(1.08)'}
                                    onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                                >
                                    {isPlaying ? <Pause size={16} /> : <Play size={16} style={{ marginLeft: '2px' }} />}
                                </button>

                                {/* Time Display */}
                                <span style={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.8rem',
                                    color: '#a0a0a0',
                                    minWidth: '85px'
                                }}>
                                    {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
                                </span>

                                {/* Seek Bar */}
                                <div
                                    onClick={handleSeek}
                                    style={{
                                        flex: 1,
                                        height: '6px',
                                        background: '#3a3a3a',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        height: '100%',
                                        width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                                        background: 'var(--primary, #dc2626)',
                                        borderRadius: '3px',
                                        transition: 'width 0.1s linear'
                                    }} />
                                </div>

                                {/* Speed Control */}
                                <button
                                    onClick={handleSpeedChange}
                                    style={{
                                        padding: '5px 10px',
                                        borderRadius: '4px',
                                        background: '#2a2a2a',
                                        border: '1px solid #3a3a3a',
                                        color: '#b0b0b0',
                                        cursor: 'pointer',
                                        fontSize: '0.75rem',
                                        fontWeight: '600',
                                        minWidth: '44px',
                                        transition: 'all 0.15s'
                                    }}
                                    onMouseOver={e => {
                                        e.currentTarget.style.background = '#353535'
                                        e.currentTarget.style.borderColor = 'var(--primary)'
                                    }}
                                    onMouseOut={e => {
                                        e.currentTarget.style.background = '#2a2a2a'
                                        e.currentTarget.style.borderColor = '#3a3a3a'
                                    }}
                                    title="Change playback speed"
                                >
                                    {playbackSpeed}x
                                </button>
                                <a
                                    href={disposition.recording_url}
                                    download
                                    title="Download recording"
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: '36px', height: '36px', borderRadius: '8px',
                                        background: '#2a2a2a', border: '1px solid #3a3a3a',
                                        color: '#ccc', textDecoration: 'none', transition: 'all 0.15s'
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.background = '#353535'; e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)' }}
                                    onMouseOut={e => { e.currentTarget.style.background = '#2a2a2a'; e.currentTarget.style.borderColor = '#3a3a3a'; e.currentTarget.style.color = '#ccc' }}
                                >
                                    <Download size={16} />
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Scrollable Content Area */}
                    <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                        {/* Transcript */}
                        <div className="transcript-container" style={{ padding: '16px' }}>
                            {loading ? (
                                <div className="loading-state"><div className="spinner"></div><p>Loading transcript...</p></div>
                            ) : error ? (
                                <div className="error-state"><p>Failed to load: {error}</p></div>
                            ) : events.length === 0 ? (
                                <div className="empty-state">
                                    <MessageSquare size={32} />
                                    <p>No transcript available</p>
                                    <small>Enable monitoring to capture transcriptions</small>
                                </div>
                            ) : (
                                <div className="chat-box">
                                {events.map((event, i) => {
                                    // Render interruption events distinctively
                                    if (event.event_type === 'interruption') {
                                        const meta = parseInterruptionMetadata(event)
                                        return (
                                            <div key={i} style={styles.interruptionRow}>
                                                <Zap size={16} />
                                                <strong>INTERRUPTED</strong>
                                                {meta?.played_seconds !== undefined && (
                                                    <span>at {meta.played_seconds.toFixed(1)}s</span>
                                                )}
                                                {meta?.remaining_audios?.length > 0 && (
                                                    <span>— {meta.remaining_audios.length} audio(s) remaining</span>
                                                )}
                                            </div>
                                        )
                                    }

                                    return (
                                        <div key={i} style={styles.messageRow}>
                                            {/* Timestamp */}
                                            <div style={styles.timestamp}>
                                                {event.call_duration_at_event !== null && event.call_duration_at_event !== undefined
                                                    ? formatTimestamp(event.call_duration_at_event)
                                                    : ''}
                                            </div>

                                            {/* Content */}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ ...getSenderStyle(event), marginBottom: '3px' }}>
                                                    {getIcon(event)}
                                                    {getSenderLabel(event)}
                                                    {event.script_step && (
                                                        <span style={styles.stepBadge}>Step {event.script_step}</span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                                                    {event.event_type === 'bot_audio' ? (
                                                        renderBotAudio(event, i)
                                                    ) : (
                                                        <span style={{
                                                            color: event.event_type === 'call_start' || event.event_type === 'call_end'
                                                                ? 'var(--text-muted)'
                                                                : 'var(--text-primary, #fff)'
                                                        }}>
                                                            {formatContent(event)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                                </div>
                            )}
                        </div>

                        {/* Keywords */}
                        {keywordList.length > 0 && (
                            <div style={{
                                padding: '12px 20px',
                                borderTop: '1px solid rgba(255,255,255,0.08)',
                                background: 'linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.05) 100%)'
                            }}>
                                <h4 style={{
                                    fontSize: '0.7rem',
                                    marginBottom: '10px',
                                    color: 'var(--text-muted)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    <Tag size={12} style={{ color: 'var(--primary)' }} /> Keywords Detected
                                </h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {keywordList.map((kw, i) => {
                                        const catColor = getKeywordCategoryColor(kw.category)
                                        return (
                                            <div key={i} style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: catColor.bg,
                                                border: `1px solid ${catColor.border}`,
                                                borderRadius: '6px',
                                                padding: '5px 10px',
                                                borderLeft: `3px solid ${catColor.color}`
                                            }}>
                                                <span style={{
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    color: catColor.color
                                                }}>
                                                    {formatLabel(kw.category)}
                                                </span>
                                                {kw.keywords.length > 0 && (
                                                    <span style={{
                                                        color: 'var(--text-secondary)',
                                                        fontSize: '0.7rem',
                                                        fontFamily: 'monospace'
                                                    }}>
                                                        {kw.keywords.slice(0, 3).map(w => `"${w}"`).join(', ')}
                                                        {kw.keywords.length > 3 && ` +${kw.keywords.length - 3}`}
                                                    </span>
                                                )}
                                                <span style={{
                                                    background: catColor.color,
                                                    color: '#fff',
                                                    padding: '1px 6px',
                                                    borderRadius: '8px',
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    marginLeft: '2px'
                                                }}>
                                                    {kw.count}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Legend */}
                        <div style={{
                            padding: '14px 20px',
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                            fontSize: '0.7rem',
                            color: 'var(--text-muted)',
                            display: 'flex',
                            gap: '20px',
                            flexWrap: 'wrap',
                            background: 'rgba(0,0,0,0.2)'
                        }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Bot size={12} style={{ color: 'var(--primary, #dc2626)' }} /> Bot</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><User size={12} style={{ color: '#22c55e' }} /> Caller (FG)</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><User size={12} style={{ color: '#f59e0b' }} /> Caller (BG)</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Zap size={12} style={{ color: '#ffc107' }} /> Interruption</span>
                            <span><span style={{ color: '#ff6b6b', textDecoration: 'line-through' }}>text</span> = Cut off</span>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}